/**
 * Bridge-less workaround for real-account sign-in.
 *
 * The PWA is designed to receive a WHB JWT from a `/bridge` route on
 * app.whitehatbots.com via `#token=<jwt>&exp=<unix>` on /auth/callback.
 * That bridge page doesn't exist in production yet. Until it does, we
 * drive the handoff from the user's side:
 *
 *   1. We open a popup pointed at app.whitehatbots.com/sign-in so the user
 *      can authenticate there normally (or confirm they already are).
 *   2. The user opens the popup's DevTools console and pastes a one-liner
 *      we provide (with a Copy button). The one-liner reads
 *      `localStorage.token`, decodes its `exp`, and navigates the popup
 *      to `${our-origin}/auth/callback#token=…&exp=…`.
 *   3. AuthCallbackPage runs inside the popup, POSTs to /api/session
 *      (setting the HttpOnly cookie for our origin), then — because it
 *      detects `window.opener` — postMessages `{ type: 'whb-auth-done',
 *      exp }` back to us and closes itself.
 *   4. We listen, flip the auth store into `real` mode, and navigate to
 *      /accounts. No page reload, no leftover popup.
 *
 * We also keep a manual paste fallback for mobile (no DevTools) and
 * environments where popups are blocked — that path reuses the same
 * /auth/callback hash flow but inside the current tab.
 *
 * When the real WHB bridge ships, SignInPage can flip back to
 * startRealLogin() and this file can be deleted — nothing downstream
 * (AuthCallbackPage, /api/session, the proxy, logout) depends on how
 * the token reached /auth/callback.
 */

import { useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from "@mui/material";
import ContentCopy from "@mui/icons-material/ContentCopy";
import OpenInNew from "@mui/icons-material/OpenInNew";
import { useAuthStore } from "@/store/auth-store";

const WHB_SIGN_IN_URL = "https://app.whitehatbots.com/sign-in";
const CALLBACK_PATH = "/auth/callback";
/** Shared with AuthCallbackPage — the popup uses this exact name so
 *  AuthCallbackPage can identify itself as the handoff popup via
 *  `window.name`, and so re-opening reuses the same window. */
const POPUP_WINDOW_NAME = "whb-signin";
/** Shared with AuthCallbackPage — primary signal back to the opener.
 *  BroadcastChannel works even if window.opener gets severed by COOP
 *  or by the popup's cross-origin navigation. */
const HANDOFF_CHANNEL = "whb-handoff";


/**
 * Build the console one-liner the user pastes in the popup's DevTools.
 *
 * Kept on a single line and using only ES5-safe constructs because the
 * user will literally paste this into a console on a page we don't
 * control. The `encodeURIComponent` guards against `+` / `/` in the
 * base64url token being eaten as URL reserved chars even though they
 * aren't used by base64url — belt and braces.
 */
function buildConsoleSnippet(callbackOrigin: string): string {
  return (
    `(function(){` +
    `var t=localStorage.token;` +
    `if(!t){alert('No WHB session found. Sign in first, then run this again.');return;}` +
    `var exp=0;try{var p=t.split('.')[1].replace(/-/g,'+').replace(/_/g,'/');` +
    `exp=(JSON.parse(atob(p+'===='.slice(0,(4-p.length%4)%4))).exp)||0;}catch(e){}` +
    `if(!exp)exp=Math.floor(Date.now()/1000)+3600;` +
    `location.href='${callbackOrigin}${CALLBACK_PATH}#token='+encodeURIComponent(t)+'&exp='+exp;` +
    `})();`
  );
}

export function WhbTokenHandoffDialog({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  const signInReal = useAuthStore((s) => s.signInReal);

  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [popupOpened, setPopupOpened] = useState(false);

  // Keep a handle on the popup so we can poke it / close it if needed.
  // The popup's own code closes itself on success, but if the user
  // cancels the dialog we want to let go of the reference.
  const popupRef = useRef<Window | null>(null);

  const callbackOrigin =
    typeof window !== "undefined" ? window.location.origin : "";
  const consoleSnippet = useMemo(
    () => buildConsoleSnippet(callbackOrigin),
    [callbackOrigin],
  );

  // Listen for the popup's `whb-auth-done` signal. Only wired while the
  // dialog is open. We subscribe on BOTH channels:
  //
  //   - BroadcastChannel("whb-handoff") — primary. Survives COOP /
  //     cross-origin navigation severing the opener link.
  //   - window.addEventListener("message") — secondary. Works when the
  //     opener relationship did survive; harmless duplicate otherwise.
  //
  // `handled` de-dupes the two channels firing back-to-back so we don't
  // signInReal + navigate twice.
  useEffect(() => {
    if (!open) return;

    let handled = false;

    function complete(exp: number) {
      if (handled) return;
      handled = true;
      // /api/session already set the HttpOnly cookie in the popup. Flip
      // our local auth store into "real" mode so the persisted state
      // (sessionStorage) is correct after the reload below.
      signInReal(null, exp);
      // DELIBERATELY do not call popupRef.current?.close() here. When
      // we called it alongside the popup self-closing via
      // AuthCallbackPage's window.close(), Chrome in some configs
      // (installed PWAs, shared browsing context groups) cascaded
      // the close into unrelated browser windows. The popup now
      // shows a "signed in, close this window" screen with a manual
      // button — no auto-close, no cascade.
      onClose();
      // Full navigation rather than a client-side route push. Data-
      // loading code that reads the auth cookie or the persisted auth
      // store runs on app boot, so a router-level `navigate('/accounts')`
      // leaves the old in-memory snapshots in place and the user sees
      // demo data until they manually refresh. A location.replace forces
      // a fresh boot that picks up the cookie and the freshly persisted
      // "real" auth state. `replace` keeps the sign-in page out of the
      // back-button history.
      window.location.replace("/accounts");
    }

    function extractExpFromData(data: unknown): number | null {
      if (!data || typeof data !== "object") return null;
      const d = data as { type?: unknown; exp?: unknown };
      if (d.type !== "whb-auth-done") return null;
      return typeof d.exp === "number"
        ? d.exp
        : Math.floor(Date.now() / 1000) + 60 * 60;
    }

    function onMessage(ev: MessageEvent) {
      if (ev.origin !== window.location.origin) return;
      const exp = extractExpFromData(ev.data);
      if (exp != null) complete(exp);
    }

    let channel: BroadcastChannel | null = null;
    function onBroadcast(ev: MessageEvent) {
      const exp = extractExpFromData(ev.data);
      if (exp != null) complete(exp);
    }
    try {
      channel = new BroadcastChannel(HANDOFF_CHANNEL);
      channel.addEventListener("message", onBroadcast);
    } catch {
      /* BroadcastChannel unsupported — postMessage path below still works */
    }

    window.addEventListener("message", onMessage);
    return () => {
      window.removeEventListener("message", onMessage);
      try {
        channel?.removeEventListener("message", onBroadcast);
        channel?.close();
      } catch {
        /* ignore */
      }
    };
  }, [open, signInReal, onClose]);

  // Reset transient state each time the dialog opens.
  useEffect(() => {
    if (open) {
      setError(null);
      setCopied(false);
      setPopupOpened(false);
    }
  }, [open]);

  // Sergey 2026-04-25: tried popup form, then naked-tab form, both
  // rendered the WHB SPA blank-blue on first load until manual F5.
  // Root cause is Chrome's "Total Cookie Protection" — cross-origin
  // tabs opened via window.open() get a PARTITIONED cookie jar (scoped
  // to "WHB-as-loaded-from-whb-client.pages.dev", which is empty), so
  // the SPA loads in a signed-out state, then never recovers when the
  // partition flips on F5. The fix is opening with `rel="noopener
  // noreferrer"` — that severs the opener relationship and strips the
  // Referer header, so Chrome treats the new tab as if the user typed
  // the URL into the address bar (un-partitioned cookies, fresh nav
  // context). Industry-standard fix: Discord, Notion, Stripe all use
  // this pattern for cross-origin handoffs. Trade-off: we lose
  // `popupRef` (no handle to the new tab), so the post-back signal
  // relies entirely on BroadcastChannel — which works fine since it's
  // opener-independent and the AuthCallbackPage broadcasts on the
  // same-origin channel.
  //
  // Implementation: render the trigger as an <a> click via a
  // programmatically-built anchor (window.open() doesn't support `rel`
  // attributes). The anchor is created, clicked, and discarded.
  function openWhbPopup() {
    const url = `${WHB_SIGN_IN_URL}?_t=${Date.now()}`;
    try {
      const a = document.createElement("a");
      a.href = url;
      a.target = POPUP_WINDOW_NAME; // keep name → re-clicks reuse the same tab
      a.rel = "noopener noreferrer";
      // The anchor must be in the document for some browsers to honor
      // the click; we add + remove in the same tick.
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } catch {
      // Last-ditch fallback if document.body isn't available somehow.
      window.open(url, POPUP_WINDOW_NAME, "noopener,noreferrer");
    }
    popupRef.current = null;
    setPopupOpened(true);
  }

  async function copySnippet() {
    try {
      await navigator.clipboard.writeText(consoleSnippet);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* Clipboard API can fail in non-secure contexts. The <code> block
         below is still selectable so the user can copy manually. */
    }
  }


  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Sign in with your existing WHB session</DialogTitle>
      <DialogContent>
        <Stack spacing={2.5} sx={{ pt: 0.5 }}>
          <Alert severity="info" variant="outlined">
            The WHB bridge page isn&apos;t live yet. As a one-time workaround,
            you sign in on whitehatbots.com in a new tab and paste a short
            command into its DevTools console — the tab hands your session
            back here, then shows a &quot;you&apos;re signed in&quot; screen with a
            button to close it.
          </Alert>

          {/* Step 1 — open WHB tab */}
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
              Step 1 — Open the WHB sign-in tab
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              Opens{" "}
              <Box component="span" sx={{ fontFamily: "monospace" }}>
                app.whitehatbots.com/sign-in
              </Box>{" "}
              in a new tab.{" "}
              <Box component="span" sx={{ fontWeight: 600, color: "text.primary" }}>
                If the tab stays blank, press{" "}
                <Box
                  component="kbd"
                  sx={{
                    fontFamily: "monospace",
                    fontSize: "0.85em",
                    px: 0.75,
                    py: 0.1,
                    border: 1,
                    borderColor: "divider",
                    borderRadius: 0.5,
                  }}
                >
                  Ctrl+F5
                </Box>{" "}
                (or{" "}
                <Box
                  component="kbd"
                  sx={{
                    fontFamily: "monospace",
                    fontSize: "0.85em",
                    px: 0.75,
                    py: 0.1,
                    border: 1,
                    borderColor: "divider",
                    borderRadius: 0.5,
                  }}
                >
                  ⇧⌘R
                </Box>{" "}
                on macOS) to force a hard reload — do this <em>before</em> opening DevTools in Step 2.
              </Box>{" "}
              Sign in there (or confirm you already are).
            </Typography>
            <Button
              variant={popupOpened ? "outlined" : "contained"}
              size="medium"
              onClick={openWhbPopup}
              startIcon={<OpenInNew sx={{ fontSize: 18 }} />}
              sx={{ textTransform: "none" }}
            >
              {popupOpened ? "Re-open WHB sign-in tab" : "Open WHB sign-in tab"}
            </Button>
          </Box>

          {/* Step 2 — paste console snippet */}
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
              Step 2 — In the popup, open DevTools and paste this
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              Press{" "}
              <Box component="kbd" sx={{ fontFamily: "monospace", fontSize: "0.85em", px: 0.75, py: 0.1, border: 1, borderColor: "divider", borderRadius: 0.5 }}>
                F12
              </Box>{" "}
              (or{" "}
              <Box component="kbd" sx={{ fontFamily: "monospace", fontSize: "0.85em", px: 0.75, py: 0.1, border: 1, borderColor: "divider", borderRadius: 0.5 }}>
                ⌥⌘I
              </Box>{" "}
              on macOS) in the WHB tab, switch to the{" "}
              <strong>Console</strong> tab, paste the line below, and hit
              Enter. The WHB tab will redirect back to this app, finish signing
              you in here, and then show a button to close itself.
            </Typography>
            <Box
              component="pre"
              sx={{
                p: 1.5,
                bgcolor: "action.hover",
                borderRadius: 1,
                fontFamily: "monospace",
                fontSize: "0.75rem",
                lineHeight: 1.45,
                whiteSpace: "pre-wrap",
                wordBreak: "break-all",
                m: 0,
                mb: 1,
                maxHeight: 140,
                overflow: "auto",
              }}
            >
              {consoleSnippet}
            </Box>
            <Button
              size="small"
              startIcon={<ContentCopy sx={{ fontSize: 16 }} />}
              onClick={copySnippet}
              sx={{ textTransform: "none" }}
            >
              {copied ? "Copied" : "Copy command"}
            </Button>
          </Box>

          {error && (
            <Alert severity="warning" variant="outlined">
              {error}
            </Alert>
          )}

          {/* Manual paste fallback was removed 2026-04-25. The mobile
           *  case is now served by the QR pairing flow (Settings →
           *  Pair another device on a desktop where the user is
           *  already signed in, then mobile scans the QR), so the
           *  paste-token-into-textfield UX is no longer needed. */}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose}>Cancel</Button>
      </DialogActions>
    </Dialog>
  );
}
